import { useState, useCallback, useRef, useEffect } from 'react';
import type { DataGridColumn } from '../DataGrid.types';

export interface UseColumnResizeOptions<T extends Record<string, unknown>> {
  columns: DataGridColumn<T>[];
  minWidth?: number;
  maxWidth?: number;
  onColumnResize?: (columnId: string, width: number) => void;
}

export interface UseColumnResizeReturn<T extends Record<string, unknown>> {
  columnWidths: Record<string, number>;
  resizingColumnId: string | null;
  getResizeHandler: (columnId: string) => {
    onMouseDown: (e: React.MouseEvent) => void;
  };
  getColumnWidth: (column: DataGridColumn<T>) => number;
  resetColumnWidths: () => void;
}

export function useColumnResize<T extends Record<string, unknown>>({
  columns,
  minWidth = 50,
  maxWidth = 500,
  onColumnResize
}: UseColumnResizeOptions<T>): UseColumnResizeReturn<T> {
  // Initialize column widths from column definitions
  const getInitialWidths = useCallback(() => {
    const widths: Record<string, number> = {};
    columns.forEach((col) => {
      widths[col.id] = col.width || 150;
    });
    return widths;
  }, [columns]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(getInitialWidths);
  const [resizingColumnId, setResizingColumnId] = useState<string | null>(null);

  // Store refs for event handlers to properly clean up
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const activeColumnIdRef = useRef<string | null>(null);
  const handlersRef = useRef<{
    moveHandler: ((e: MouseEvent) => void) | null;
    upHandler: (() => void) | null;
  }>({ moveHandler: null, upHandler: null });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (handlersRef.current.moveHandler) {
        document.removeEventListener('mousemove', handlersRef.current.moveHandler);
      }
      if (handlersRef.current.upHandler) {
        document.removeEventListener('mouseup', handlersRef.current.upHandler);
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  const getResizeHandler = useCallback((columnId: string) => {
    const onMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      startXRef.current = e.clientX;
      startWidthRef.current = columnWidths[columnId] || 150;
      activeColumnIdRef.current = columnId;
      setResizingColumnId(columnId);

      // Set cursor style during resize
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const moveHandler = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startXRef.current;
        const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidthRef.current + delta));

        setColumnWidths((prev) => ({
          ...prev,
          [columnId]: newWidth
        }));
      };

      const upHandler = () => {
        setResizingColumnId(null);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        // Remove event listeners using stored references
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);

        // Clear stored references
        handlersRef.current.moveHandler = null;
        handlersRef.current.upHandler = null;
        activeColumnIdRef.current = null;

        // Notify parent
        setColumnWidths((current) => {
          if (onColumnResize) {
            onColumnResize(columnId, current[columnId]);
          }
          return current;
        });
      };

      // Store handlers for cleanup
      handlersRef.current.moveHandler = moveHandler;
      handlersRef.current.upHandler = upHandler;

      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
    };

    return { onMouseDown };
  }, [columnWidths, minWidth, maxWidth, onColumnResize]);

  const getColumnWidth = useCallback((column: DataGridColumn<T>): number => {
    return columnWidths[column.id] || column.width || 150;
  }, [columnWidths]);

  const resetColumnWidths = useCallback(() => {
    setColumnWidths(getInitialWidths());
  }, [getInitialWidths]);

  return {
    columnWidths,
    resizingColumnId,
    getResizeHandler,
    getColumnWidth,
    resetColumnWidths
  };
}
